import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, MapPin, CalendarDays } from 'lucide-react';
import {
  CalendarEvent,
  CalendarCategory,
} from '../../services/calendarService';
import EventThumbnail from './EventThumbnail';
import {
  ACCENT,
  dateBadgeParts,
  eventKey,
  flagEmoji,
  formatDateRange,
  resolveCategory,
} from './eventUtils';

interface FeaturedEventsSliderProps {
  events: CalendarEvent[];
  categoryById: Map<string, CalendarCategory>;
  onOpenEvent?: (event: CalendarEvent) => void;
}

/**
 * Horizontaler Slider mit großen Featured-Event-Karten.
 * Wird von der Seite ausgeblendet, wenn keine Featured-Events existieren.
 */
const FeaturedEventsSlider: React.FC<FeaturedEventsSliderProps> = ({
  events,
  categoryById,
  onOpenEvent,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);

  if (events.length === 0) return null;

  const scrollBy = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth, 640), behavior: 'smooth' });
  };

  return (
    <section className="max-w-5xl mx-auto px-3 sm:px-4 pt-6" aria-label="Featured Events">
      <div className="flex items-center justify-between mb-3">
        <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-white">
          <Sparkles size={18} style={{ color: ACCENT }} />
          Highlights
        </h2>
        {events.length > 2 && (
          <div className="hidden sm:flex items-center gap-1">
            <button
              onClick={() => scrollBy(-1)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-zinc-800 transition-colors"
              aria-label="Zurück scrollen"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-zinc-800 transition-colors"
              aria-label="Weiter scrollen"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory pb-2 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none' }}
      >
        {events.map((event) => {
          const badge = dateBadgeParts(event);
          const flag = flagEmoji(event.countryCode);
          const category = resolveCategory(event, categoryById);
          const locationShort = [event.city, event.country].filter(Boolean).join(', ');

          return (
            <button
              key={eventKey(event)}
              onClick={() => onOpenEvent?.(event)}
              className="group relative flex-shrink-0 w-[280px] sm:w-[360px] h-44 sm:h-52 rounded-2xl overflow-hidden snap-start border border-zinc-800 hover:border-zinc-600 transition-colors text-left"
            >
              <EventThumbnail
                event={event}
                category={category}
                className="absolute inset-0 w-full h-full transition-transform duration-500 group-hover:scale-105"
                iconSize={40}
              />
              {/* Gradient-Overlay für Lesbarkeit */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/10 pointer-events-none" />

              {/* Datum-Badge */}
              {badge && (
                <div className="absolute top-2.5 left-2.5 rounded-xl bg-black/70 backdrop-blur-sm px-2.5 py-1.5 text-center min-w-[46px]">
                  <div className="text-sm font-extrabold leading-tight" style={{ color: ACCENT }}>
                    {badge.top}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-300 leading-tight">
                    {badge.bottom}
                  </div>
                </div>
              )}

              {/* Organisation-Badge */}
              {event.organization && (
                <div className="absolute top-2.5 right-2.5 rounded-full bg-black/70 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  {event.organization}
                </div>
              )}

              {/* Text unten */}
              <div className="absolute inset-x-0 bottom-0 p-3.5">
                <h3 className="text-white font-bold text-base sm:text-lg leading-snug line-clamp-2 mb-1">
                  {event.title || 'Event'}
                </h3>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-300">
                  <span className="flex items-center gap-1">
                    <CalendarDays size={12} className="flex-shrink-0" />
                    {formatDateRange(event)}
                  </span>
                  {locationShort && (
                    <span className="flex items-center gap-1 min-w-0">
                      <MapPin size={12} className="flex-shrink-0" />
                      <span className="truncate">
                        {flag ? `${flag} ` : ''}
                        {locationShort}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default FeaturedEventsSlider;
